import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWordPressPostBySlug } from '../../services/wordpress';
import { ArrowLeft, Calendar, Tag, Clock, Newspaper } from 'lucide-react';
import { SEO } from '../../components/seo/SEO';
import { PageLayout } from '../../components/layout/PageLayout';
import { ArticleSkeleton } from '../../components/skeletons/ArticleSkeleton';
import { AdSense } from '../../components/ads/AdSense';
import { ImageModal } from '../../components/modals/ImageModal';

interface WordPressTag {
  id: number;
  name: string;
}

// Utilisation de type 'any' pour éviter les problèmes de typage
// avec les données provenant de l'API WordPress
type WordPressPost = any;

export function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<WordPressPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const fetchedPost = await getWordPressPostBySlug(slug as string);
        console.log('Post reçu dans le composant:', fetchedPost);
        console.log('Données de l\'auteur:', fetchedPost._embedded?.author);
        setPost(fetchedPost);
      } catch (error) {
        // Ignorer l'erreur ici, juste rediriger
        navigate('/actualites');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, navigate]);

  useEffect(() => {
    // Ajouter les gestionnaires de clic sur les images après le rendu du contenu
    const addImageClickHandlers = () => {
      const articleImages = document.querySelectorAll('.prose img');
      articleImages.forEach((img) => {
        if (img instanceof HTMLImageElement) {
          img.style.cursor = 'zoom-in';
          img.addEventListener('click', () => {
            setSelectedImage({
              url: img.src,
              alt: img.alt || 'Image de l\'article'
            });
          });
        }
      });
    };

    if (post) {
      // Attendre que le DOM soit mis à jour
      setTimeout(addImageClickHandlers, 100);
    }

    return () => {
      // Nettoyer les gestionnaires lors du démontage
      const articleImages = document.querySelectorAll('.prose img');
      articleImages.forEach((img) => {
        if (img instanceof HTMLImageElement) {
          img.style.cursor = '';
          img.removeEventListener('click', () => {});
        }
      });
    };
  }, [post]);

  // Calcul de la durée de lecture (environ 250 mots/min)
  function getReadingTime(htmlContent: string) {
    const text = htmlContent.replace(/<[^>]*>/g, ' ');
    const wordCount = text.trim().split(/\s+/).length;
    return Math.ceil(wordCount / 250);
  }

  const cleanHtml = (html: string) => {
    return html.replace(/<[^>]*>/g, '').trim();
  };

  if (loading) {
    return <ArticleSkeleton />;
  }

  if (!post) {
    return <div>Article non trouvé</div>;
  }

  const seoData = {
    title: post.yoast_head_json?.title || cleanHtml(post.title.rendered),
    description: post.yoast_head_json?.description || cleanHtml(post.excerpt.rendered).substring(0, 160),
    og_title: post.yoast_head_json?.og_title || cleanHtml(post.title.rendered),
    og_description: post.yoast_head_json?.og_description || cleanHtml(post.excerpt.rendered).substring(0, 160),
    og_image: post.yoast_head_json?.og_image?.[0] || post.featured_media_url,
    article: true,
    published_time: post.date,
    modified_time: post.modified,
    tags: post.tags?.map((tag: WordPressTag) => tag.name) || []
  };

  const readingTime = getReadingTime(post.content.rendered);

  // Dans le rendu, ajoutons un log pour vérifier les données au moment de l'affichage
  if (post?._embedded?.author) {
    console.log('Données de l\'auteur au moment du rendu:', post._embedded.author);
  }

  return (
    <>
      <SEO {...seoData} />
      
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: 'Actualités', path: '/actualites', icon: <Newspaper className="w-4 h-4" /> },
            { label: cleanHtml(post.title.rendered), path: `/actualites/${post.slug}` }
          ]
        }}
        className="py-4 sm:py-6 lg:py-8"
      >
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden max-w-5xl mx-auto">
          {/* En-tête de l'article */}
          <div className="px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 lg:pt-10 pb-8 sm:pb-10 lg:pb-12 bg-gradient-to-br from-primary/5 to-primary/10">
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6 leading-tight"
              dangerouslySetInnerHTML={{ __html: post.title.rendered }}
            />

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-neutral-600">
              <time dateTime={post.date} className="flex items-center gap-1.5 bg-white/50 px-3 py-1.5 rounded-full">
                <Calendar className="w-4 h-4 text-primary" />
                {new Date(post.date).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </time>
              <div className="flex items-center gap-1.5 bg-white/50 px-3 py-1.5 rounded-full">
                <Clock className="w-4 h-4 text-primary" />
                <span>{readingTime} min de lecture</span>
              </div>
              {post._embedded?.author?.[0] && (
                <div className="flex items-center gap-2 bg-white/50 px-3 py-1.5 rounded-full">
                  {post._embedded.author[0].avatar_urls?.['96'] && (
                    <img
                      src={post._embedded.author[0].avatar_urls['96']}
                      alt={`Avatar de ${post._embedded.author[0].name}`}
                      className="w-5 h-5 rounded-full"
                    />
                  )}
                  <span className="font-medium">Par {post._embedded.author[0].name}</span>
                </div>
              )}
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags.map((tag: WordPressTag) => (
                  <span 
                    key={tag.id}
                    className="bg-white/50 px-3 py-1.5 rounded-full text-sm text-neutral-700 font-medium flex items-center gap-1.5"
                  >
                    <Tag className="w-3.5 h-3.5 text-primary" />
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Image principale avec overlay gradient */}
          {post._embedded?.['wp:featuredmedia']?.[0] && (
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-10" />
              <img
                src={post._embedded['wp:featuredmedia'][0].source_url}
                alt={post._embedded['wp:featuredmedia'][0].alt_text || cleanHtml(post.title.rendered)}
                className="w-full h-auto object-contain"
              />
            </div>
          )}

          {/* Annonce AdSense */}
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="max-w-3xl mx-auto">
              <AdSense adSlot="1424794127" id="article-main" />
            </div>
          </div>

          {/* Contenu de l'article */}
          <div className="px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
            <article
              className="prose prose-lg max-w-3xl mx-auto
                // Titres
                prose-h1:text-3xl sm:prose-h1:text-4xl prose-h1:font-bold prose-h1:text-neutral-900 prose-h1:mb-6
                prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:font-bold prose-h2:text-neutral-800 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:font-semibold prose-h3:text-neutral-800 prose-h3:mt-8 prose-h3:mb-4
                prose-h4:text-lg sm:prose-h4:text-xl prose-h4:font-semibold prose-h4:text-neutral-700 prose-h4:mt-6 prose-h4:mb-4
                
                // Paragraphes
                prose-p:text-neutral-600 prose-p:leading-relaxed prose-p:text-base sm:prose-p:text-lg
                prose-p:mb-4 [&_p:not(:last-child)]:mb-6
                [&_p+p]:mt-6
                
                // Texte en gras et liens
                prose-strong:text-primary-dark prose-strong:font-semibold
                prose-a:text-primary prose-a:no-underline hover:prose-a:text-primary-dark
                prose-a:transition-colors prose-a:border-b prose-a:border-primary/30
                hover:prose-a:border-primary-dark
                
                // Images et figures
                prose-img:rounded-xl prose-img:my-8 prose-img:mx-auto 
                prose-img:shadow-lg prose-img:w-auto prose-img:h-auto
                prose-img:max-w-full
                prose-figure:my-12 prose-figure:mx-auto prose-figure:text-center 
                prose-figure:w-full
                prose-figcaption:text-center prose-figcaption:text-neutral-500 
                prose-figcaption:mt-3 prose-figcaption:text-base
                
                // Support spécifique des images WordPress
                [&_.wp-block-image]:my-12 [&_.wp-block-image]:mx-auto [&_.wp-block-image]:w-full
                [&_.wp-block-image.size-full]:w-full [&_.wp-block-image.size-full]:max-w-none
                [&_.wp-block-image_img]:mx-auto [&_.wp-block-image_img]:block
                [&_.wp-block-image_img]:w-full [&_.wp-block-image_img]:h-auto
                [&_.wp-block-image.aligncenter]:text-center [&_.wp-block-image.aligncenter_img]:mx-auto
                [&_.wp-block-image.alignfull_img]:w-full
                [&_.wp-block-image_figure]:m-0 [&_.wp-block-image_figure]:flex 
                [&_.wp-block-image_figure]:flex-col [&_.wp-block-image_figure]:items-center
                [&_.wp-block-image_figure]:w-full
                
                // Galeries WordPress
                [&_.wp-block-gallery]:my-12 [&_.wp-block-gallery]:mx-auto [&_.wp-block-gallery]:max-w-4xl
                [&_.blocks-gallery-grid]:grid [&_.blocks-gallery-grid]:gap-6 
                [&_.blocks-gallery-grid]:w-full [&_.blocks-gallery-grid]:mx-auto
                [&_.blocks-gallery-grid]:grid-cols-1 
                sm:[&_.blocks-gallery-grid]:grid-cols-2 
                lg:[&_.blocks-gallery-grid]:grid-cols-3
                [&_.blocks-gallery-grid.columns-1]:!grid-cols-1
                [&_.blocks-gallery-grid.columns-2]:!grid-cols-2
                [&_.blocks-gallery-grid.columns-3]:!grid-cols-3
                [&_.blocks-gallery-grid.columns-4]:!grid-cols-4
                
                // Support des éléments de galerie individuels
                [&_.blocks-gallery-item]:relative [&_.blocks-gallery-item]:overflow-hidden
                [&_.blocks-gallery-item]:rounded-xl [&_.blocks-gallery-item]:shadow-lg
                [&_.blocks-gallery-item]:w-full
                [&_.blocks-gallery-item_figure]:m-0 [&_.blocks-gallery-item_figure]:w-full 
                [&_.blocks-gallery-item_figure]:flex
                [&_.blocks-gallery-item_figure]:justify-center [&_.blocks-gallery-item_figure]:items-center
                [&_.blocks-gallery-item_img]:w-full [&_.blocks-gallery-item_img]:h-auto 
                [&_.blocks-gallery-item_img]:object-contain [&_.blocks-gallery-item_img]:max-h-[600px]

                // Support des nouveaux blocs galerie WordPress
                [&_.wp-block-gallery.has-nested-images]:grid
                [&_.wp-block-gallery.has-nested-images]:gap-6 [&_.wp-block-gallery.has-nested-images]:mx-auto
                [&_.wp-block-gallery.has-nested-images]:grid-cols-1
                sm:[&_.wp-block-gallery.has-nested-images]:grid-cols-2
                lg:[&_.wp-block-gallery.has-nested-images]:grid-cols-3
                [&_.wp-block-gallery.has-nested-images.columns-1]:!grid-cols-1
                [&_.wp-block-gallery.has-nested-images.columns-2]:!grid-cols-2
                [&_.wp-block-gallery.has-nested-images.columns-3]:!grid-cols-3
                [&_.wp-block-gallery.has-nested-images.columns-4]:!grid-cols-4
                [&_.wp-block-gallery.has-nested-images_figure]:m-0 
                [&_.wp-block-gallery.has-nested-images_figure]:w-full
                [&_.wp-block-gallery.has-nested-images_figure]:flex
                [&_.wp-block-gallery.has-nested-images_figure]:justify-center
                [&_.wp-block-gallery.has-nested-images_figure]:items-center
                [&_.wp-block-gallery.has-nested-images_img]:w-full [&_.wp-block-gallery.has-nested-images_img]:h-auto
                [&_.wp-block-gallery.has-nested-images_img]:object-contain [&_.wp-block-gallery.has-nested-images_img]:max-h-[600px]
                [&_.wp-block-gallery.has-nested-images_img]:rounded-xl [&_.wp-block-gallery.has-nested-images_img]:shadow-lg

                // Styles des légendes de galerie
                [&_.blocks-gallery-item_figcaption]:absolute [&_.blocks-gallery-item_figcaption]:bottom-0
                [&_.blocks-gallery-item_figcaption]:left-0 [&_.blocks-gallery-item_figcaption]:right-0
                [&_.blocks-gallery-item_figcaption]:bg-black/50 [&_.blocks-gallery-item_figcaption]:text-white
                [&_.blocks-gallery-item_figcaption]:p-2 [&_.blocks-gallery-item_figcaption]:text-sm
                [&_.wp-block-gallery_figcaption]:mt-4 [&_.wp-block-gallery_figcaption]:text-center
                [&_.wp-block-gallery_figcaption]:text-neutral-500 [&_.wp-block-gallery_figcaption]:text-base

                // Listes
                prose-ul:space-y-2 prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
                prose-ol:space-y-2 prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-neutral-600 prose-li:text-base sm:prose-li:text-lg prose-li:leading-relaxed
                [&_ul_li]:relative [&_ul_li]:pl-2
                [&_ul_li]:before:hidden
                [&_ol_li]:relative [&_ol_li]:pl-2

                // Support des listes WordPress
                [&_.wp-block-list]:space-y-2 [&_.wp-block-list]:my-6
                [&_.wp-block-list_ul]:list-disc [&_.wp-block-list_ul]:pl-6 [&_.wp-block-list_ul]:space-y-2
                [&_.wp-block-list_ol]:list-decimal [&_.wp-block-list_ol]:pl-6 [&_.wp-block-list_ol]:space-y-2
                [&_.wp-block-list_li]:text-neutral-600 [&_.wp-block-list_li]:text-base 
                sm:[&_.wp-block-list_li]:text-lg [&_.wp-block-list_li]:leading-relaxed

                // Support des listes imbriquées
                [&_ul_ul]:mt-2 [&_ul_ul]:mb-0 [&_ul_ul]:ml-6
                [&_ol_ol]:mt-2 [&_ol_ol]:mb-0 [&_ol_ol]:ml-6
                [&_ol_ul]:mt-2 [&_ol_ul]:mb-0 [&_ol_ul]:ml-6
                [&_ul_ol]:mt-2 [&_ul_ol]:mb-0 [&_ul_ol]:ml-6
                
                // Citations
                prose-blockquote:border-l-4 prose-blockquote:border-primary 
                prose-blockquote:bg-primary/5 prose-blockquote:px-6 
                prose-blockquote:py-4 prose-blockquote:my-8
                prose-blockquote:text-neutral-700 prose-blockquote:text-lg
                prose-blockquote:leading-relaxed prose-blockquote:not-italic
                
                // Séparateurs
                prose-hr:border-neutral-200 prose-hr:my-12
                
                // Classes WordPress spécifiques
                [&_.wp-block-quote]:border-l-4 [&_.wp-block-quote]:border-primary
                [&_.wp-block-quote]:bg-primary/5 [&_.wp-block-quote]:p-6
                [&_.wp-block-quote]:rounded-r-lg [&_.wp-block-quote]:my-8
                
                [&_.wp-block-image]:my-12 [&_.wp-block-image_img]:mx-auto
                [&_.aligncenter]:mx-auto [&_.alignfull]:w-full
                [&_.alignwide]:max-w-4xl [&_.alignwide]:mx-auto
                
                // Listes personnalisées
                [&_ul_li]:flex [&_ul_li]:items-start
                [&_ul_li]:before:content-['•'] [&_ul_li]:before:text-primary 
                [&_ul_li]:before:mr-3 [&_ul_li]:before:font-bold 
                [&_ul_li]:before:text-xl [&_ul_li]:before:leading-tight
                
                // Support des blocs WordPress
                [&_.wp-block-heading]:font-bold
                [&_h2.wp-block-heading]:text-3xl [&_h2.wp-block-heading]:font-bold 
                [&_h2.wp-block-heading]:text-neutral-800 [&_h2.wp-block-heading]:mt-12 
                [&_h2.wp-block-heading]:mb-6
                
                [&_h3.wp-block-heading]:text-2xl [&_h3.wp-block-heading]:font-semibold 
                [&_h3.wp-block-heading]:text-neutral-800 [&_h3.wp-block-heading]:mt-8 
                [&_h3.wp-block-heading]:mb-4
                
                [&_h4.wp-block-heading]:text-xl [&_h4.wp-block-heading]:font-semibold 
                [&_h4.wp-block-heading]:text-neutral-700 [&_h4.wp-block-heading]:mt-6 
                [&_h4.wp-block-heading]:mb-4
                
                // Tables
                prose-table:mx-auto prose-table:my-8 prose-table:border-collapse
                prose-table:w-full prose-table:max-w-full prose-table:table-fixed
                prose-thead:bg-[#f9fafb]
                prose-th:p-4 prose-th:text-left prose-th:font-semibold 
                prose-th:text-gray-900
                prose-td:p-4 prose-td:text-gray-900
                prose-tr:bg-white
                prose-tr:nth-child(odd):bg-[#f9fafb]
                [&_table]:w-full [&_table]:max-w-full [&_table]:my-8 [&_table]:border-collapse
                [&_table]:bg-white [&_table]:rounded-lg [&_table]:overflow-hidden
                [&_table_tr]:bg-white
                [&_table_tr:nth-child(odd)]:bg-[#f9fafb]
                [&_table_tr_td]:p-4 [&_table_tr_th]:p-4
                [&_table_tr_td]:align-middle [&_table_tr_th]:align-middle
                [&_table_tr_td]:w-[33.33%] [&_table_tr_th]:w-[33.33%]
                
                // Support des tables WordPress
                [&_.wp-block-table]:w-full [&_.wp-block-table]:max-w-full [&_.wp-block-table]:my-8
                [&_.wp-block-table_table]:w-full [&_.wp-block-table_table]:border-collapse
                [&_.wp-block-table_table]:bg-white [&_.wp-block-table_table]:rounded-lg 
                [&_.wp-block-table_table]:overflow-hidden
                [&_.wp-block-table_thead]:bg-[#f9fafb]
                [&_.wp-block-table_th]:p-4 [&_.wp-block-table_th]:text-left 
                [&_.wp-block-table_th]:font-semibold [&_.wp-block-table_th]:text-gray-900
                [&_.wp-block-table_td]:p-4 [&_.wp-block-table_td]:text-gray-900
                [&_.wp-block-table_tr]:bg-white
                [&_.wp-block-table_tr:nth-child(odd)]:bg-[#f9fafb]
                [&_.wp-block-table_tr_td]:align-middle [&_.wp-block-table_tr_th]:align-middle
                [&_.wp-block-table_tr_td]:w-[33.33%] [&_.wp-block-table_tr_th]:w-[33.33%]
                [&_.wp-block-table_td_ul]:list-none [&_.wp-block-table_td_ul]:m-0 
                [&_.wp-block-table_td_ul]:p-0 [&_.wp-block-table_td_ul]:space-y-2
                [&_.wp-block-table_td_li]:flex [&_.wp-block-table_td_li]:items-center 
                [&_.wp-block-table_td_li]:gap-2
                [&_.wp-block-table_td_img]:w-6 [&_.wp-block-table_td_img]:h-6 
                [&_.wp-block-table_td_img]:inline-block [&_.wp-block-table_td_img]:m-0

                // Support des vidéos WordPress
                [&_.wp-block-video]:my-12 [&_.wp-block-video]:mx-auto [&_.wp-block-video]:max-w-4xl
                [&_.wp-block-video_video]:w-full [&_.wp-block-video_video]:h-auto
                [&_.wp-block-video_video]:rounded-xl [&_.wp-block-video_video]:shadow-lg
                [&_.wp-block-video_figcaption]:mt-4 [&_.wp-block-video_figcaption]:text-center
                [&_.wp-block-video_figcaption]:text-neutral-500 [&_.wp-block-video_figcaption]:text-base

                // Support des iframes (pour les vidéos YouTube, etc.)
                [&_iframe]:w-full [&_iframe]:max-w-4xl [&_iframe]:mx-auto [&_iframe]:my-12
                [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:shadow-lg

                [&_.wp-block-embed]:my-12 [&_.wp-block-embed]:mx-auto [&_.wp-block-embed]:max-w-4xl
                [&_.wp-block-embed_iframe]:w-full [&_.wp-block-embed_iframe]:h-auto
                [&_.wp-block-embed_iframe]:rounded-xl [&_.wp-block-embed_iframe]:shadow-lg
                [&_.wp-block-embed_figcaption]:mt-4 [&_.wp-block-embed_figcaption]:text-center
                [&_.wp-block-embed_figcaption]:text-neutral-500 [&_.wp-block-embed_figcaption]:text-base

                [&_.wp-block-paragraph]:mb-8
                [&_.wp-block-paragraph:not(:last-child)]:mb-8
                [&_.wp-block-paragraph+.wp-block-paragraph]:mt-8

                // Support des groupes WordPress
                [&_.wp-block-group]:my-8 [&_.wp-block-group]:w-full
                [&_.wp-block-group.is-layout-flow]:space-y-4
                [&_.wp-block-group.is-layout-flex]:flex [&_.wp-block-group.is-layout-flex]:flex-wrap
                [&_.wp-block-group.is-layout-constrained]:max-w-4xl [&_.wp-block-group.is-layout-constrained]:mx-auto
                [&_.wp-block-group.alignfull]:max-w-none [&_.wp-block-group.alignwide]:max-w-6xl

                // Support des colonnes WordPress
                [&_.wp-block-columns]:flex [&_.wp-block-columns]:flex-wrap [&_.wp-block-columns]:gap-8
                [&_.wp-block-column]:flex-1 [&_.wp-block-column]:min-w-[300px]
                [&_.wp-block-column]:flex [&_.wp-block-column]:flex-col [&_.wp-block-column]:gap-4

                // Ajustements pour les images dans les groupes et colonnes
                [&_.wp-block-image]:my-4 [&_.wp-block-image]:mx-auto
                [&_.wp-block-image.aligncenter]:text-center [&_.wp-block-image.aligncenter_img]:mx-auto
                [&_.wp-block-image.alignfull_img]:w-full
                [&_.wp-block-image_img]:max-h-[500px] [&_.wp-block-image_img]:w-auto [&_.wp-block-image_img]:object-contain
                [&_.wp-block-image_figure]:m-0 [&_.wp-block-image_figure]:inline-flex [&_.wp-block-image_figure]:flex-col [&_.wp-block-image_figure]:items-center

                // Support des galeries amélioré
                [&_.wp-block-gallery:not(:last-child)]:mb-8
                [&_.wp-block-gallery.is-cropped_.blocks-gallery-item_img]:aspect-auto
                [&_.blocks-gallery-grid]:grid [&_.blocks-gallery-grid]:gap-6
                [&_.blocks-gallery-grid:not([class*='columns-'])]:grid-cols-1
                md:[&_.blocks-gallery-grid:not([class*='columns-'])]:grid-cols-2
                lg:[&_.blocks-gallery-grid:not([class*='columns-'])]:grid-cols-3
                [&_.blocks-gallery-grid.columns-1]:grid-cols-1
                [&_.blocks-gallery-grid.columns-2]:grid-cols-2
                [&_.blocks-gallery-grid.columns-3]:grid-cols-3
                [&_.blocks-gallery-grid.columns-4]:grid-cols-4

                // Styles des légendes
                [&_.wp-block-image_figcaption]:mt-2 [&_.wp-block-image_figcaption]:text-sm
                [&_.wp-block-image_figcaption]:text-center [&_.wp-block-image_figcaption]:text-neutral-600
                [&_.wp-block-gallery_figcaption]:mt-4 [&_.wp-block-gallery_figcaption]:text-base
                [&_.blocks-gallery-item_figcaption]:text-sm [&_.blocks-gallery-item_figcaption]:p-2

                // Support des espacements WordPress
                [&_.has-medium-gap]:gap-4
                [&_.has-large-gap]:gap-6
                [&_.has-larger-gap]:gap-8"
              dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            />

            {/* Navigation retour */}
            <div className="border-t border-neutral-200 pt-8 mt-12 max-w-3xl mx-auto">
              <Link
                to="/actualites"
                className="inline-flex items-center text-primary hover:text-primary-dark group bg-primary/5 px-4 py-2.5 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">Retour aux actualités</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Modal pour les images */}
        <ImageModal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          imageUrl={selectedImage?.url || ''}
          imageAlt={selectedImage?.alt || ''}
        />
      </PageLayout>
    </>
  );
}
